/**
 * Hybrid storage that uses sessionStorage for tab-specific state
 * and localStorage for persistence across browser sessions.
 *
 * Behavior:
 * - Each tab maintains its own state in sessionStorage
 * - On first load in a tab, falls back to localStorage
 * - Writes to both storages to ensure persistence
 *
 * This prevents:
 * - Multiple tabs syncing restaurant selection (confusing UX)
 *
 * While maintaining:
 * - Persistence when closing/reopening browser
 */
import type { Storage } from "redux-persist";

const SESSION_KEY_PREFIX = "session:";
const LOCAL_KEY_PREFIX = "persist:";

export const createHybridStorage = (): Storage => {
  return {
    async getItem(key: string): Promise<string | null> {
      // 1. First, check sessionStorage (current tab's state)
      const sessionKey = SESSION_KEY_PREFIX + key;
      const sessionValue = sessionStorage.getItem(sessionKey);

      if (sessionValue !== null) {
        // Tab already has its own state
        return sessionValue;
      }

      // 2. Fall back to localStorage (restore from previous session)
      const localKey = LOCAL_KEY_PREFIX + key;
      const localValue = localStorage.getItem(localKey);

      if (localValue !== null) {
        // Found in localStorage, copy to sessionStorage for this tab
        // This "hydrates" the current tab with the last saved state
        sessionStorage.setItem(sessionKey, localValue);
        return localValue;
      }

      return null;
    },

    async setItem(key: string, value: string): Promise<void> {
      const sessionKey = SESSION_KEY_PREFIX + key;
      const localKey = LOCAL_KEY_PREFIX + key;

      // Write to BOTH storages:
      // - sessionStorage: for this tab's current state
      // - localStorage: for persistence across browser sessions
      sessionStorage.setItem(sessionKey, value);
      localStorage.setItem(localKey, value);
    },

    async removeItem(key: string): Promise<void> {
      const sessionKey = SESSION_KEY_PREFIX + key;
      const localKey = LOCAL_KEY_PREFIX + key;

      // Clear from both storages
      sessionStorage.removeItem(sessionKey);
      localStorage.removeItem(localKey);
    },
  };
};
